import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { TourProvider } from "@/components/tour/TourProvider";
import { Toaster } from "@/components/ui/sonner";
import { DialogHost } from "@/lib/confirm";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollToTop from "@/components/site/ScrollToTop";
import PresenceHeartbeat from "@/components/site/PresenceHeartbeat";
import WhatsAppFab from "@/components/site/WhatsAppFab";
import CookieBanner from "@/components/site/CookieBanner";
import PortalRedirect from "@/components/site/PortalRedirect";
import HealthcareProfessionalGate from "@/components/site/HealthcareProfessionalGate";
import ImpersonationBanner from "@/components/admin/ImpersonationBanner";
import ImpersonationSessionPill from "@/components/admin/ImpersonationSessionPill";
import ImpersonationLand from "@/pages/ImpersonationLand";
import { SandboxBanner, SandboxFloatingPill } from "@/components/sandbox/SandboxBanner";
import SandboxLanding from "@/pages/public/SandboxLanding";
import CertificateVerifyPage from "@/pages/public/CertificateVerifyPage";

import LandingPage from "@/pages/Landing";
import FindDoctor from "@/pages/FindDoctor";
import BookAppointment from "@/pages/BookAppointment";
import Login from "@/pages/Login";
import HowItWorks from "@/pages/HowItWorks";
import FAQ from "@/pages/FAQ";
import SmileAssessment from "@/pages/SmileAssessment";
import SmileSimulatorPage from "@/pages/SmileSimulatorPage";
import DoctorLandingPage from "@/pages/DoctorLandingPage";
import PublicArticlePage from "@/pages/PublicArticlePage";
import PublicCasePage from "@/pages/PublicCasePage";
import LegacyDoctorSlugRedirect from "@/components/routing/LegacyDoctorSlugRedirect";
import HowChipsWork from "@/pages/HowChipsWork";
import Contact from "@/pages/Contact";
import ForDoctors from "@/pages/ForDoctors";
import ApplyAsProvider from "@/pages/ApplyAsProvider";
import DoctorSignupPage from "@/pages/DoctorSignupPage";
import SuperAdminEnrol from "@/pages/super-admin/SuperAdminEnrol";
import SuperAdminLogin from "@/pages/super-admin/SuperAdminLogin";
import SuperAdminHome, { SuperAdminSecurity } from "@/pages/super-admin/SuperAdminHome";
import Shop from "@/pages/Shop";
import InvisiqueInfinite from "@/pages/InvisiqueInfinite";
import InvisiqueSecurePage from "@/pages/InvisiqueSecurePage";
import InvisiqueSerenePage from "@/pages/InvisiqueSerenePage";
import InvisiqueApexPage from "@/pages/InvisiqueApexPage";
import ProductPage from "@/pages/ProductPage";
import WhyInvisique from "@/pages/WhyInvisique";
import Pricing from "@/pages/Pricing";
// CityLanding (city-specific SEO pages) parked 2026-02-28 — kept on disk.
// import CityLanding from "@/pages/CityLanding";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Refund from "@/pages/legal/Refund";

import PortalLayout from "@/pages/PortalLayout";
import PatientDashboard from "@/pages/patient/Dashboard";
import PatientShell from "@/pages/patient/PatientShell";
import PatientAppointments from "@/pages/patient/Appointments";
import PatientChat from "@/pages/patient/Chat";
import PatientClinicChat from "@/pages/patient/ClinicChat";
import PatientProfile from "@/pages/patient/Profile";
// iter98.99f3 — Patient-side compliance coach chat (Phase 4)
import PatientCoachChat from "@/pages/patient/CoachChat";

import DoctorDashboard from "@/pages/doctor/Dashboard";
import DoctorAppointments from "@/pages/doctor/Appointments";
import DoctorPatients from "@/pages/doctor/Patients";
import PatientsImport from "@/pages/doctor/PatientsImport";
import DoctorCases from "@/pages/doctor/Cases";
import DoctorCasesPipeline from "@/pages/doctor/CasesPipeline";
import DoctorCaseDetail from "@/pages/doctor/CaseDetail";
import RequestRevisionPage from "@/pages/doctor/RequestRevisionPage";
import TreatmentPlanFullView from "@/pages/doctor/TreatmentPlanFullView";
import CaseSubmissionPage from "@/pages/doctor/CaseSubmissionPage";
import DoctorLabChat from "@/pages/doctor/LabChat";
import DoctorPatientChat from "@/pages/doctor/PatientChat";
import DoctorOffers from "@/pages/doctor/Offers";
// iter98.99f3 — Doctor escalations inbox (Phase 4)
import DoctorEscalationsInbox from "@/pages/doctor/EscalationsInbox";
import DoctorTourLibrary from "@/pages/doctor/TourLibrary";
import DoctorProfile from "@/pages/doctor/Profile";
import LandingPageEditor from "@/pages/doctor/LandingPageEditor";
import ContentEditorPage from "@/pages/doctor/ContentEditorPage";
import LearningAcademyCatalogue from "@/pages/doctor/LearningAcademyCatalogue";
import LearningAcademy from "@/pages/doctor/LearningAcademy";
import AcademyModulePlayer from "@/pages/doctor/AcademyModulePlayer";
import DoctorPatientProfile from "@/pages/doctor/PatientProfile";
import DoctorBilling from "@/pages/doctor/Billing";
import DoctorShell from "@/pages/doctor/DoctorShell";

// iter100.113 — Certification Author portal (/creator/*)
import CreatorShell from "@/pages/creator/CreatorShell";
import CourseDashboard from "@/pages/creator/CourseDashboard";
import CourseSetup from "@/pages/creator/CourseSetup";
import CourseBuilder from "@/pages/creator/CourseBuilder";

// iter100.113 Phase 3 — Admin Course Reviews
// iter100.228 — CourseReviewInbox now only rendered inside AdminApprovals.
// import CourseReviewInbox from "@/pages/admin/CourseReviewInbox";
import CourseReviewDetail from "@/pages/admin/CourseReviewDetail";
// iter100.156 — Admin Case Validations (course-case approval queue)
// iter100.228 — CaseValidationInbox now only rendered inside AdminApprovals.
// import CaseValidationInbox from "@/pages/admin/CaseValidationInbox";
// iter100.156f — Live Sessions / Webinars (shared by Author + Admin)
import WebinarManager from "@/pages/staff/WebinarManager";

// iter100.113 Phase 3 — Learner-side DB-driven course viewer
import CourseView from "@/pages/doctor/CourseView";
import PatientMyCase from "@/pages/patient/MyCase";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/Overview";
import AdminLeads from "@/pages/admin/Leads";
import AdminAppointments from "@/pages/admin/Appointments";
import AdminPatients from "@/pages/admin/Patients";
import AdminImpersonationLog from "@/pages/admin/ImpersonationLog";
import AdminPatientProfile from "@/pages/admin/PatientProfile";
import AdminDoctors from "@/pages/admin/Doctors";
import AdminDoctorProfile from "@/pages/admin/DoctorProfile";
import AdminContentReview from "@/pages/admin/AdminContentReview";
import AdminApprovals from "@/pages/admin/AdminApprovals";
import LandingCms from "@/pages/admin/LandingCms";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BlogManager from "@/pages/admin/BlogManager";
import BlogAuthor from "@/pages/creator/BlogAuthor";
import AdminCoe from "@/pages/admin/CoeTeam";
import AdminCases from "@/pages/admin/Cases";
import AdminBilling from "@/pages/admin/Billing";
import AdminFinance from "@/pages/admin/Finance";
import AdminInventory from "@/pages/admin/Inventory";
import ServiceSetup from "@/pages/admin/ServiceSetup";
import AdminCaseDetail from "@/pages/admin/CaseDetailV2";
import AdminMessages from "@/pages/admin/Messages";
import AdminApplications from "@/pages/admin/Applications";
import AdminLeadsHub from "@/pages/admin/LeadsHub";
import AdminLeadDetail from "@/pages/admin/LeadDetail";
import AdminLeadsReports from "@/pages/admin/LeadsReports";
import AdminCoeMemberProfile from "@/pages/admin/CoeMemberProfile";
import AdminCoeReports from "@/pages/admin/CoeReports";
import AcceptInvite from "@/pages/AcceptInvite";
import AdminAuditLog from "@/pages/admin/AuditLog";
import AdminActivityHistory from "@/pages/admin/ActivityHistory";
import AdminWhatsApp from "@/pages/admin/WhatsApp";
// iter100.250 — AdminReferrals restored as a first-class page after the
// LeadsHub-embedded "Referrals" tab caused a jarring "two pages in one"
// visual mismatch (LeadsHub's clean grammar vs. AdminReferrals's older
// standalone chrome). Referrals is genuinely a different sub-app
// (doctor demo-link tracking, reward approvals, invitations), so it
// gets its own /admin/referrals route again with its own sidebar item.
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminPermissions from "@/pages/admin/Permissions";
import AdminAlignerTracking from "@/pages/admin/tracking/AlignerTracking";
import AdminPatientAnalytics from "@/pages/admin/tracking/PatientAnalytics";
import AdminPricing from "@/pages/admin/Pricing";
import AdminScanVisits from "@/pages/admin/ScanVisits";
// iter100.227 — /admin/technicians consolidated into Team & Access; standalone
// AdminTechnicians page deleted. Route below now redirects to the People tab.
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminPresence from "@/pages/admin/Presence";
import AdminDangerZone from "@/pages/admin/DangerZone";

import CoeLayout from "@/pages/coe/CoeLayout";
import CoeDashboard from "@/pages/coe/CoeDashboard"; // eslint-disable-line no-unused-vars -- legacy lobby (kept for rollback)
import UnifiedCoeDashboard from "@/pages/coe/UnifiedDashboard";
import CoeLeadsList from "@/pages/coe/CoeLeadsList";
import CoeLeadDetail from "@/pages/coe/CoeLeadDetail";
import PlannerQueue from "@/pages/coe/PlannerQueue";
import PlannerCaseDetail from "@/pages/coe/PlannerCaseDetail";
import PlannerProviderChat from "@/pages/coe/PlannerProviderChat";
import StaffProfile from "@/pages/coe/StaffProfile";
import ProductionQueue from "@/pages/coe/ProductionQueue";
import ProductionDashboard from "@/pages/coe/ProductionDashboard";
import ProductionNotifications from "@/pages/coe/ProductionNotifications";
import ProductionMyStats from "@/pages/coe/ProductionMyStats";
import ProductionCaseDetail from "@/pages/coe/ProductionCaseDetail";
import TechnicianDashboard from "@/pages/coe/TechnicianDashboard";
import TechnicianVisits from "@/pages/coe/TechnicianVisits";
import TechnicianVisitDetail from "@/pages/coe/TechnicianVisitDetail";
import SalesPortal from "@/pages/coe/SalesPortal";
import B2BLeadsList from "@/pages/coe/B2BLeadsList";import B2BLeadDetail from "@/pages/coe/B2BLeadDetail";
import AdminB2BLeads from "@/pages/admin/B2BLeads";
import AdminB2BLeadDetail from "@/pages/admin/B2BLeadDetail";
import SupportTickets from "@/pages/coe/SupportTickets";
import QaInbox from "@/pages/coe/QaInbox";
import QaCaseDetail from "@/pages/coe/QaCaseDetail";
// iter98.99f1 — CoE Compliance Monitoring & Support queue (Phase 2)
import CoeComplianceQueue from "@/pages/coe/ComplianceQueue";
import CoeEscalations from "@/pages/coe/Escalations";
// iter98.99f2 — Coach case detail (Phase 3: chat + notes + escalate)
import CoeComplianceCaseDetail from "@/pages/coe/ComplianceCaseDetail";
// iter98.99ea — PES shared inbox (self-claim web/b2b/application leads)
import PesInbox from "@/pages/coe/Inbox";

// iter98.52 — Sales Portal (CRM for aligner doctor activation)
import SalesLayout from "@/pages/sales/SalesLayout";
import SalesDashboard from "@/pages/sales/SalesDashboard";
import SalesPipeline from "@/pages/sales/SalesPipeline";
import SalesProspects from "@/pages/sales/SalesProspects";
// iter98.53 — Phase 2 pages (Tasks + Analytics)
import SalesTasks from "@/pages/sales/SalesTasks";
import SalesAnalytics from "@/pages/sales/SalesAnalytics";
import SalesDemoLinks from "@/pages/sales/SalesDemoLinks";
// iter100.228 — AdminReferrals import removed; component now only used inside
// LeadsHub as the "Referrals" tab. See /app/frontend/src/pages/admin/LeadsHub.jsx.

import ErrorBoundary from "@/components/site/ErrorBoundary";
import PortalLandingIntro from "@/components/site/PortalLandingIntro";
import AnnouncementBar from "@/components/site/AnnouncementBar";

const SiteShell = ({ children }) => (
    <>
        <AnnouncementBar />
        <Header />
        <main className="min-h-[calc(100vh-5rem)]">
            <ErrorBoundary routeLabel="this page">
                {children}
            </ErrorBoundary>
        </main>
        <Footer />
    </>
);

// Legacy URL → unified Leads Hub detail redirect helper.
import { useParams } from "react-router-dom";
function AppRedirect({ kind }) {
    const { id } = useParams();
    return <Navigate to={`/admin/leads/${kind}/${id}`} replace />;
}

/* iter100.124 — Legacy `/products/:slug` marketing URLs redirect to the
   new canonical `/mouthguards-and-retainer/:slug` path so external
   bookmarks and old email links keep working.
   iter100.145 — Route slug renamed `guards-and-retainer` →
   `mouthguards-and-retainer` to match the "Mouthguards & Retainer"
   mega-menu label. Both legacy paths (`/products/*` and
   `/guards-and-retainer/*`) redirect here. */
function LegacyProductRedirect() {
    const { slug } = useParams();
    return <Navigate to={`/mouthguards-and-retainer/${slug}`} replace />;
}

function App() {
    /* iter98.82 — single-domain deploy. Every route mounts on every
     * host (Emergent supports only one custom domain per deployment).
     * `host-mode.js` is now a permanent compatibility shim. */
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <TourProvider>
                    <ScrollToTop />
                    <PresenceHeartbeat />
                    <ImpersonationBanner />
                    {/* iter101.4 — Persistent floating pill in impersonated
                        tabs so operators always know which identity they're
                        viewing. No-ops in normal (non-impersonated) tabs. */}
                    <ImpersonationSessionPill />
                    {/* iter98.93 — Sandbox banner + floating pill. Render
                        ABOVE Routes so they appear on every portal page
                        when the current user has is_demo=true (sandbox
                        visitor who claimed a magic link). Real doctors
                        never see them — the components no-op when
                        !user.is_demo. */}
                    <SandboxBanner />
                    <SandboxFloatingPill />
                    <Routes>
                        {/* ─────────────────────────────────────────────────────────
                            Public sandbox landing (no auth required)
                            ───────────────────────────────────────────────────────── */}
                        <Route path="/sandbox/:token" element={<SandboxLanding />} />

                        {/* iter100.113s — Public certificate verify page.
                            Reachable from the QR code on the PDF cert +
                            shareable URL (invisique.com/verify/INV-…).
                            No auth. Serves as a growth loop landing page. */}
                        <Route path="/verify/:certNumber" element={<CertificateVerifyPage />} />

                        {/* iter98.99bc — Stand-alone treatment-plan document
                            (auth-gated by token in localStorage, opened in a
                            new tab from the doctor's Plan Approval Hero).
                            Lives OUTSIDE /doctor so the portal sidebar isn't
                            rendered — gives the page a clean "printable
                            document" feel. */}
                        <Route path="/treatment-plan/:caseId" element={<TreatmentPlanFullView />} />

                        {/* iter100.104 — Full-screen kiosk Academy Module Player.
                            Lives OUTSIDE /doctor so the portal sidebar + topbar
                            are hidden for an immersive learning experience. */}
                        <Route path="/academy/module/:moduleId" element={<AcademyModulePlayer />} />

                        {/* iter100.112 — ICPP course-detail page uses a
                            course-scoped left sidebar and its own topbar
                            instead of the shared PortalLayout. Lives at
                            /doctor/academy/icpp but rendered outside the
                            portal shell so the Coursera-style course-nav
                            can own the full viewport. */}
                        <Route path="/doctor/academy/icpp" element={<DoctorShell><LearningAcademy /></DoctorShell>} />

                        {/* iter100.113 Phase 3 — DB-driven course viewer for the
                            doctor's Learning Academy. Opens in a new tab from the
                            catalogue so the doctor's clinical workspace is preserved.
                            Uses DoctorShell for auth guard, but lives OUTSIDE the
                            main PortalLayout so it can own the full viewport (its
                            own topbar + course-scoped sidebar). */}
                        <Route path="/doctor/academy/courses/:courseId" element={<DoctorShell><CourseView /></DoctorShell>} />

                        {/* iter100.113 — Certification Author portal.
                            /creator/* mounts under its own layout shell
                            (CreatorShell) — gated to role='staff' with
                            staff_type='course_creator', or admin. Screens:
                              /creator                    Dashboard
                              /creator/courses            Same as dashboard (alias)
                              /creator/courses/new        New course setup
                              /creator/courses/:id/setup  Edit setup
                              /creator/courses/:id        Full builder (split view)
                        */}
                        <Route path="/creator" element={<CreatorShell />}>
                            {/* iter100.116 — Dashboard and My Courses were literally
                                the same page. Redirect the index to /creator/courses
                                so there's one canonical list, no duplicate nav. */}
                            <Route index element={<Navigate to="/creator/courses" replace />} />
                            <Route path="courses" element={<CourseDashboard />} />
                            <Route path="courses/new" element={<CourseSetup />} />
                            <Route path="courses/:courseId/setup" element={<CourseSetup />} />
                            <Route path="courses/:courseId" element={<CourseBuilder />} />
                            {/* iter100.154 — Blog authoring under the same portal.
                                Approval workflow mirrors Courses. */}
                            <Route path="blog" element={<BlogAuthor />} />
                            {/* iter100.156f — Live Sessions manager (shared with admin) */}
                            <Route path="webinars" element={<WebinarManager />} />
                            {/* iter100.171 — Certification Authors share the doctor
                                content editorial review pool with admins. Same
                                UI as /admin/content-review; RBAC is enforced
                                server-side via require_content_reviewer. */}
                            <Route path="content-review" element={<AdminContentReview />} />
                            {/* iter100.115 — Certification Authors share the same
                                StaffProfile page all CoE staff use, keeping the
                                portal chrome consistent with Planner et al. */}
                            <Route path="profile" element={<StaffProfile />} />
                        </Route>

                        {/* ─────────────────────────────────────────────────────────
                            Marketing site routes
                            ───────────────────────────────────────────────────────── */}
                        <Route path="/" element={<SiteShell><PortalRedirect><LandingPage /></PortalRedirect></SiteShell>} />
                        <Route path="/why-invisique" element={<SiteShell><WhyInvisique /></SiteShell>} />
                        <Route path="/how-it-works" element={<SiteShell><HowItWorks /></SiteShell>} />
                        <Route path="/find-doctor" element={<SiteShell><FindDoctor /></SiteShell>} />
                        {/* City-specific landing pages parked 2026-02-28.
                            Routes (/find-doctor/:city, /clear-aligners-in/:city)
                            and the corresponding footer / FindDoctor strips are
                            disabled until we run a proper SEO content + keyword
                            audit. `CityLanding.jsx` and `data/cities.js` are
                            preserved in the codebase for that rev. */}
                        <Route path="/faq" element={<SiteShell><FAQ /></SiteShell>} />
                        <Route path="/blog" element={<SiteShell><Blog /></SiteShell>} />
                        <Route path="/blog/:slug" element={<SiteShell><BlogPost /></SiteShell>} />
                        <Route path="/smile-assessment" element={<SiteShell><SmileAssessment /></SiteShell>} />
                        <Route path="/smile-simulator" element={<SiteShell><SmileSimulatorPage /></SiteShell>} />
                        {/* iter100.3 — Doctor's own branded smile-sim page.
                            iter100.260 — Canonical prefix changed from /s to /dr
                            (semantic + medical). Old /s/ routes redirect via
                            the LegacyDoctorSlugRedirect helper below so all
                            printed cards, WhatsApp shares and Google entries
                            keep working. */}
                        <Route path="/dr/:slug" element={<SiteShell><DoctorLandingPage /></SiteShell>} />
                        <Route path="/dr/:doctorSlug/article/:articleSlug" element={<PublicArticlePage />} />
                        <Route path="/dr/:doctorSlug/case/:caseSlug" element={<PublicCasePage />} />
                        {/* Legacy /s/… routes — permanent client-side redirect to /dr/…
                            with the leading `dr-` stripped from the slug. */}
                        <Route path="/s/:slug" element={<LegacyDoctorSlugRedirect kind="landing" />} />
                        <Route path="/s/:doctorSlug/article/:articleSlug" element={<LegacyDoctorSlugRedirect kind="article" />} />
                        <Route path="/s/:doctorSlug/case/:caseSlug" element={<LegacyDoctorSlugRedirect kind="case" />} />
                        {/* iter100.51 — Public trust-asset explaining the two-tier chip system. */}
                        <Route path="/how-chips-work" element={<SiteShell><HowChipsWork /></SiteShell>} />
                        <Route path="/contact" element={<SiteShell><Contact /></SiteShell>} />
                        <Route path="/for-doctors" element={<SiteShell><HealthcareProfessionalGate><ForDoctors /></HealthcareProfessionalGate></SiteShell>} />
                        {/* Legacy ortho-only URL — kept as a permanent redirect so external
                            bookmarks + Google's index keep working. The new canonical is
                            /for-doctors which welcomes both dentists + orthodontists. */}
                        <Route path="/for-orthodontists" element={<Navigate to="/for-doctors" replace />} />
                        {/* Path B Phase 2 — magic-link invite landing.
                            Sales rep emails a 14-day token → lead lands here →
                            ApplyWizard prefilled with the lead's identity
                            (email/phone locked). Wrapped in HCP gate same as
                            /for-doctors. */}
                        <Route path="/apply-as-provider/:token" element={<SiteShell><HealthcareProfessionalGate><ApplyAsProvider /></HealthcareProfessionalGate></SiteShell>} />
                        {/* iter98.99fm — Super-admin Passkey/WebAuthn routes.
                            Enrol page is public (auth is the invite token). Home and Security
                            require a super_admin JWT — the pages redirect to /super-admin-login on 401/403. */}
                        <Route path="/super-admin-login" element={<SuperAdminLogin />} />
                        {/* iter101.3 — Impersonation landing page: /admin/doctors and
                            /admin/patients open new tabs at this URL with `?token=<JWT>`
                            so the tab drops into the target's portal with a per-tab
                            sessionStorage JWT (admin's own localStorage token stays). */}
                        <Route path="/impersonate-land" element={<ImpersonationLand />} />
                        <Route path="/super-admin/enrol/:token" element={<SuperAdminEnrol />} />
                        <Route path="/super-admin" element={<SuperAdminHome />} />
                        <Route path="/super-admin/security" element={<SuperAdminSecurity />} />
                        <Route path="/for-providers" element={<SiteShell><HealthcareProfessionalGate><ForDoctors /></HealthcareProfessionalGate></SiteShell>} />
                        <Route path="/become-a-provider" element={<SiteShell><HealthcareProfessionalGate><ForDoctors /></HealthcareProfessionalGate></SiteShell>} />
                        <Route path="/pricing" element={<SiteShell><Pricing /></SiteShell>} />
                        <Route path="/shop" element={<SiteShell><Shop /></SiteShell>} />
                        <Route path="/infinite" element={<SiteShell><InvisiqueInfinite /></SiteShell>} />
                        {/* Legacy URL → canonical /infinite (avoid duplicate-content + keep
                            external bookmarks/SEO traffic working). 2026-02-25 polish pass. */}
                        <Route path="/invisique-infinite" element={<Navigate to="/infinite" replace />} />
                        {/* iter100.122 — Products mega-menu marketing pages.
                            Night Guard / Anti-Snoring / Sports Guard get a
                            shared `ProductPage` shell with a per-product
                            enquiry form. Retainer keeps its dedicated
                            `/infinite` page (deeper marketing).
                            iter100.123 — Night Guard promoted to the branded
                            "Invisique Secure" 13-section marketing page.
                            iter100.124 — Canonical URLs renamed
                            `/products/*` → `/guards-and-retainer/*` per user
                            request. Legacy `/products/*` routes redirect.
                            iter100.137 — Anti-Snoring Guard also promoted
                            to the rich "Invisique Serene" 13-section page.
                            iter100.138 — Retainer product also lives under
                            /guards-and-retainer/retainer (renders the same
                            `InvisiqueInfinite` component). Legacy `/infinite`
                            stays as an alias so bookmarks keep working.
                            iter100.145 — Slug renamed once more:
                            `/guards-and-retainer/*` →
                            `/mouthguards-and-retainer/*` to match the
                            renamed "Mouthguards & Retainer" mega-menu.
                            Old `/guards-and-retainer/*` paths silently
                            redirect so bookmarks + `#guided-callback-anchor`
                            deep-links keep working. */}
                        <Route path="/mouthguards-and-retainer/night-guard" element={<SiteShell><InvisiqueSecurePage /></SiteShell>} />
                        <Route path="/mouthguards-and-retainer/anti-snoring" element={<SiteShell><InvisiqueSerenePage /></SiteShell>} />
                        <Route path="/mouthguards-and-retainer/sports-guard" element={<SiteShell><InvisiqueApexPage /></SiteShell>} />
                        <Route path="/mouthguards-and-retainer/retainer" element={<SiteShell><InvisiqueInfinite /></SiteShell>} />
                        <Route path="/mouthguards-and-retainer/:slug" element={<SiteShell><ProductPage /></SiteShell>} />
                        {/* Legacy redirects — preserve external bookmarks / SEO
                            + any pre-rename mega-menu deep-links. */}
                        <Route path="/guards-and-retainer/night-guard" element={<Navigate to="/mouthguards-and-retainer/night-guard" replace />} />
                        <Route path="/guards-and-retainer/anti-snoring" element={<Navigate to="/mouthguards-and-retainer/anti-snoring" replace />} />
                        <Route path="/guards-and-retainer/sports-guard" element={<Navigate to="/mouthguards-and-retainer/sports-guard" replace />} />
                        <Route path="/guards-and-retainer/retainer" element={<Navigate to="/mouthguards-and-retainer/retainer" replace />} />
                        <Route path="/guards-and-retainer/:slug" element={<LegacyProductRedirect />} />
                        <Route path="/products/night-guard" element={<Navigate to="/mouthguards-and-retainer/night-guard" replace />} />
                        <Route path="/products/:slug" element={<LegacyProductRedirect />} />
                        <Route path="/privacy" element={<SiteShell><Privacy /></SiteShell>} />
                        <Route path="/terms" element={<SiteShell><Terms /></SiteShell>} />
                        <Route path="/refund-policy" element={<SiteShell><Refund /></SiteShell>} />
                        <Route path="/book/:doctorId" element={<SiteShell><BookAppointment /></SiteShell>} />

                        {/* ─────────────────────────────────────────────────────────
                            Portal routes
                            ───────────────────────────────────────────────────────── */}
                        {/* Login surfaces — stripped of the marketing
                            `<SiteShell>` so the patient portal renders as a clean
                            app shell just like Doctor / Admin / COE portals.
                            `/login` kept as a backward-compat redirect for existing
                            patient bookmarks; canonical is now `/patient-login`. */}
                        <Route path="/login" element={<Navigate to="/patient-login" replace />} />
                        <Route path="/patient-login" element={<PortalLandingIntro><Login /></PortalLandingIntro>} />
                        <Route path="/doctor-login" element={<PortalLandingIntro><Login /></PortalLandingIntro>} />
                        <Route path="/doctor-signup" element={<DoctorSignupPage />} />
                        <Route path="/admin-login" element={<PortalLandingIntro><Login /></PortalLandingIntro>} />
                        <Route path="/coe-login" element={<PortalLandingIntro><Login /></PortalLandingIntro>} />
                        {/* iter98.52 — Sales Portal login surface. Same OTP flow,
                            different role gate. */}
                        <Route path="/sales-login" element={<PortalLandingIntro><Login /></PortalLandingIntro>} />
                        <Route path="/coe/accept-invite/:token" element={<AcceptInvite />} />

                        {/* Portal landing — when someone hits my.invisique.in/
                            with no path, send them to the right login (or, if
                            already signed in, PortalRedirect will hop them to
                            their dashboard).
                            iter98.82 — Single-domain deploy: the marketing
                            `/` route owns the homepage, so this portal-root
                            short-circuit is no longer wired. */}
                        <Route path="/patient" element={<PatientShell><PortalLayout role="patient" /></PatientShell>}>
                            <Route index element={<PatientDashboard />} />
                            <Route path="appointments" element={<PatientAppointments />} />
                            <Route path="my-case" element={<PatientMyCase />} />
                            <Route path="chat" element={<PatientChat />} />
                            <Route path="clinic-chat" element={<PatientClinicChat />} />
                            <Route path="profile" element={<PatientProfile />} />
                            {/* iter98.99f3 — Patient↔coach chat (Phase 4) */}
                            <Route path="coach" element={<PatientCoachChat />} />
                        </Route>

                        <Route path="/doctor" element={<DoctorShell><PortalLayout role="doctor" /></DoctorShell>}>
                            <Route index element={<DoctorDashboard />} />
                            <Route path="appointments" element={<DoctorAppointments />} />
                            <Route path="patients" element={<DoctorPatients />} />
                            <Route path="patients/import" element={<PatientsImport />} />
                            <Route path="patients/:patientId" element={<DoctorPatientProfile />} />
                            <Route path="lab-chat" element={<DoctorLabChat />} />
                            <Route path="patient-chat" element={<DoctorPatientChat />} />
                            {/* iter98.23: New 3-view Pipeline / Compliance / Schedule design.
                                Legacy 2-tab `<DoctorCases />` preserved as `/cases-legacy` for
                                quick rollback comparison. */}
                            <Route path="cases" element={<DoctorCasesPipeline />} />
                            <Route path="cases-legacy" element={<DoctorCases />} />
                            <Route path="cases/new" element={<CaseSubmissionPage />} />
                            <Route path="cases/:caseId" element={<DoctorCaseDetail />} />
                            <Route path="cases/:caseId/request-revision" element={<RequestRevisionPage />} />
                            {/* iter98.99bl — Legacy /review-approval URL kept
                                as a redirect so any bookmarked link or
                                deep-link in an old email still lands the
                                doctor on the current case page (the review-
                                approval flow now lives inline on Treatment
                                Plan tab via PlannerResponseHero). */}
                            <Route path="cases/:caseId/review-approval" element={<Navigate to=".." replace relative="path" />} />
                            <Route path="billing" element={<DoctorBilling />} />
                            <Route path="offers" element={<DoctorOffers />} />
                            {/* iter100.3 — Doctor's own smile-simulator landing page.
                                iter100.32 — Consolidated: /doctor/landing-page now
                                renders the tabbed editor directly (Overview +
                                Design + Articles + Case Studies). Legacy
                                /doctor/landing-page/edit URL still resolves
                                for any bookmarked deep-links.
                                iter100.34 — Reviews is a tab inside the editor;
                                legacy /doctor/reviews redirects to the tab. */}
                            <Route path="landing-page" element={<LandingPageEditor />} />
                            <Route path="landing-page/edit" element={<LandingPageEditor />} />
                            {/* iter100.46 (post-review) — Dedicated full-tab
                                editor for long-form article + case study
                                writing. Opened in a NEW TAB from the
                                LandingPageEditor's Add / Edit / Draft-this
                                actions so the doctor has a real writing
                                canvas + live public preview. Testimonials
                                intentionally stay inline (short quotes). */}
                            <Route path="content/:kind/new" element={<ContentEditorPage />} />
                            <Route path="content/:kind/:id/edit" element={<ContentEditorPage />} />
                            <Route path="reviews" element={<Navigate to="/doctor/landing-page?tab=reviews" replace />} />
                            {/* 2026-07-14 — Learning Academy has 2 levels:
                                Level 1 (/doctor/academy) — Course Catalogue
                                (stays inside PortalLayout — browse view)
                                Level 2 (/doctor/academy/icpp) — ICPP course,
                                moved to its own top-level route above so it
                                can render the course-scoped left sidebar
                                outside the shared portal shell. */}
                            <Route path="academy" element={<LearningAcademyCatalogue />} />
                            {/* iter98.99f3 — Doctor compliance escalations inbox (Phase 4) */}
                            <Route path="escalations" element={<DoctorEscalationsInbox />} />
                            <Route path="tours" element={<DoctorTourLibrary />} />
                            <Route path="profile" element={<DoctorProfile />} />
                            {/* Backwards-compat: deep-links into the old
                                standalone /practice-locations and /clinic-staff
                                pages bounce into the Profile tabs that now own
                                those CRUD flows. */}
                            <Route path="practice-locations" element={<Navigate to="/doctor/profile?tab=locations" replace />} />
                            <Route path="clinic-staff" element={<Navigate to="/doctor/profile?tab=team" replace />} />
                        </Route>

                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<AdminOverview />} />
                            <Route path="leads" element={<AdminLeadsHub />} />
                            <Route path="leads/reports" element={<AdminLeadsReports />} />
                            <Route path="leads/:kind/:id" element={<AdminLeadDetail />} />
                            <Route path="leads-legacy" element={<AdminLeads />} />
                            <Route path="appointments" element={<AdminAppointments />} />
                            <Route path="scan-visits" element={<AdminScanVisits />} />
                            {/* iter100.227 — /admin/technicians consolidated into
                                Team & Access. Redirect legacy bookmarks to the
                                People tab pre-filtered to scan technicians. */}
                            <Route
                                path="technicians"
                                element={<Navigate to="/admin/coe?tab=people&filter=technician" replace />}
                            />
                            <Route path="patients" element={<AdminPatients />} />
                            <Route path="patients/:id" element={<AdminPatientProfile />} />
                            <Route path="doctors" element={<AdminDoctors />} />
                            <Route path="doctors/:id" element={<AdminDoctorProfile />} />
                            <Route path="providers" element={<AdminDoctors />} />
                            <Route path="providers/:id" element={<AdminDoctorProfile />} />
                            {/* iter101.4 — Impersonation audit log (admins only). */}
                            <Route path="impersonation-log" element={<AdminImpersonationLog />} />
                            {/* iter100.228 — Approvals command center consolidates
                                Content Review + Course Reviews + Case Validations into
                                one tabbed inbox. Legacy routes below redirect here. */}
                            <Route path="approvals" element={<AdminApprovals />} />
                            <Route path="content-review" element={<Navigate to="/admin/approvals?tab=content" replace />} />
                            <Route path="landing-page" element={<LandingCms />} />
                            <Route path="blog" element={<BlogManager />} />
                            {/* iter100.113 Phase 3 — Certification Author submissions */}
                            <Route path="course-reviews" element={<Navigate to="/admin/approvals?tab=courses" replace />} />
                            <Route path="course-reviews/:courseId" element={<CourseReviewDetail />} />
                            {/* iter100.156 — Course Case Validation queue */}
                            <Route path="case-validations" element={<Navigate to="/admin/approvals?tab=case-proofs" replace />} />
                            {/* iter100.156f — Live Sessions manager (shared route) */}
                            <Route path="webinars" element={<WebinarManager />} />
                            {/* iter101.32 — Pricing + Plan Templates consolidated
                                into the new /admin/service-setup hub. Legacy
                                deep-links redirect below. */}
                            <Route path="pricing" element={<Navigate to="/admin/service-setup?tab=pricing" replace />} />
                            <Route path="cases" element={<AdminCases />} />
                            <Route path="cases/:id" element={<AdminCaseDetail />} />
                            <Route path="billing" element={<Navigate to="/admin/finance?tab=b2b-billing" replace />} />
                            <Route path="finance" element={<AdminFinance />} />
                            <Route path="service-setup" element={<ServiceSetup />} />
                            <Route path="inventory" element={<AdminInventory />} />
                            <Route path="plan-authoring" element={<Navigate to="/admin/service-setup?tab=plan-templates" replace />} />
                            {/* Legacy Orders routes — redirect to the unified Case Dashboard. */}
                            <Route path="orders/new" element={<AdminCases />} />
                            <Route path="orders/inhouse-planning" element={<AdminCases />} />
                            <Route path="orders/planning-outsourced" element={<AdminCases />} />
                            <Route path="orders/inhouse-production" element={<AdminCases />} />
                            <Route path="orders/production-outsourced" element={<AdminCases />} />
                            {/* iter97.21 — Production Dashboard route now redirects to the
                                unified Production Control Center (cases) */}
                            <Route path="production" element={<Navigate to="/admin/cases" replace />} />
                            <Route path="tracking/aligner" element={<AdminAlignerTracking />} />
                            <Route path="tracking/patient-analytics" element={<AdminPatientAnalytics />} />
                            <Route path="coe" element={<AdminCoe />} />
                            <Route path="coe/reports" element={<AdminCoeReports />} />
                            <Route path="coe/people/:id" element={<AdminCoeMemberProfile />} />
                            <Route path="messages" element={<AdminMessages />} />
                            {/* iter98.65 — Danger zone: one-button reset demo state */}
                            <Route path="danger-zone" element={<AdminDangerZone />} />
                            {/* iter98.43 — Admin Feedback Listing removed
                               per user request (2026-06-13). The floating
                               feedback widget had been retired earlier;
                               keeping the listing page added clutter to
                               the admin nav without a way for new
                               submissions to land in it. */}
                            <Route path="feedback" element={<Navigate to="/admin" replace />} />
                            {/* Legacy: applications + b2b-leads consolidated into /admin/leads */}
                            <Route path="applications" element={<Navigate to="/admin/leads?tab=application" replace />} />
                            <Route path="applications/:id" element={<AppRedirect kind="application" />} />
                            <Route path="b2b-leads" element={<Navigate to="/admin/leads?tab=b2b" replace />} />
                            <Route path="b2b-leads/:id" element={<AppRedirect kind="b2b" />} />
                            <Route path="applications-legacy" element={<AdminApplications />} />
                            <Route path="b2b-leads-legacy" element={<AdminB2BLeads />} />
                            <Route path="b2b-leads-legacy/:id" element={<AdminB2BLeadDetail />} />
                            <Route path="audit" element={<AdminAuditLog />} />
                            <Route path="activity-history" element={<AdminActivityHistory />} />
                            <Route path="whatsapp" element={<AdminWhatsApp />} />
                            {/* iter100.262 — Verification queue was merged
                                into Providers as a 5th tile ("Pending
                                verification"). Legacy bookmark preserved
                                via redirect. */}
                            <Route path="doctors/verification" element={<Navigate to="/admin/providers?filter=pending" replace />} />
                            <Route path="analytics" element={<AdminAnalytics />} />
                            <Route path="presence" element={<AdminPresence />} />
                            {/* iter100.250 — Restored /admin/referrals as a
                                dedicated page. Previously redirected to the
                                LeadsHub Referrals tab (iter100.228) but that
                                caused a visual "two pages in one" mismatch. */}
                            <Route path="referrals" element={<AdminReferrals />} />
                            {/* iter100.203 — /admin/demo-links merged into referrals. */}
                            <Route path="demo-links" element={<Navigate to="/admin/referrals" replace />} />
                            {/* 2026-07-07 — Legacy routes for the standalone
                                permissions matrix. Now surfaced as a tab
                                inside Team & Access; these redirects keep
                                any external bookmarks working. */}
                            <Route path="permissions" element={<Navigate to="/admin/coe?tab=permissions-matrix" replace />} />
                            <Route path="access-control" element={<Navigate to="/admin/coe?tab=permissions-matrix" replace />} />
                        </Route>

                        <Route path="/coe" element={<CoeLayout />}>
                            <Route index element={<UnifiedCoeDashboard />} />
                            <Route path="profile" element={<StaffProfile />} />
                            <Route path="leads" element={<CoeLeadsList />} />
                            <Route path="leads/:id" element={<CoeLeadDetail />} />
                            {/* iter98.99ea — PES shared inbox: claim unassigned
                                web / b2b / application leads without waiting
                                for admin. 24h-stale claims auto-release. */}
                            <Route path="inbox" element={<PesInbox />} />
                            <Route path="planner" element={<UnifiedCoeDashboard />} />
                            <Route path="planner/queue" element={<PlannerQueue />} />
                            <Route path="planner/provider-chat" element={<PlannerProviderChat />} />
                            <Route path="planner/cases/:id" element={<PlannerCaseDetail />} />
                            <Route path="production" element={<ProductionDashboard />} />
                            <Route path="production/queue" element={<ProductionQueue />} />
                            <Route path="production/notifications" element={<ProductionNotifications />} />
                            <Route path="production/my-stats" element={<ProductionMyStats />} />
                            <Route path="production/case/:caseId" element={<ProductionCaseDetail />} />
                            <Route path="technician" element={<TechnicianDashboard />} />
                            <Route path="technician/visits" element={<TechnicianVisits />} />
                            <Route path="technician/visits/:visitId" element={<TechnicianVisitDetail />} />
                            <Route path="sales" element={<SalesPortal />} />
                            <Route path="sales/b2b-leads" element={<B2BLeadsList />} />
                            <Route path="sales/b2b-leads/:id" element={<B2BLeadDetail />} />
                            {/* iter98.99ev — PES-scoped B2B lead detail.
                                The PES claims a B2B lead from the Shared
                                Inbox and lands here. Backend permission is
                                via hub_assignee_id, not assigned_sales_id. */}
                            <Route path="b2b-leads/:id" element={<B2BLeadDetail scope="coe" />} />
                            <Route path="support" element={<SupportTickets />} />
                            <Route path="qa" element={<QaInbox />} />
                            <Route path="qa/cases/:id" element={<QaCaseDetail />} />
                            {/* iter98.99f1 — CoE Compliance Monitoring & Support queue (Phase 2) */}
                            <Route path="compliance" element={<CoeComplianceQueue />} />
                            <Route path="escalations" element={<CoeEscalations />} />
                            {/* iter98.99f2 — Coach case detail (Phase 3) */}
                            <Route path="compliance/cases/:id" element={<CoeComplianceCaseDetail />} />
                        </Route>

                        {/* iter98.52 — Sales Portal: aligner-doctor activation CRM.
                            Gate enforced inside SalesLayout (only sales / sales_manager
                            / admin pass through). Settings link in the sidebar is a
                            placeholder that bounces back to the dashboard until the
                            Phase 2 Settings page lands. */}
                        <Route path="/sales" element={<SalesLayout />}>
                            <Route index element={<SalesDashboard />} />
                            <Route path="pipeline" element={<SalesPipeline />} />
                            <Route path="prospects" element={<SalesProspects />} />
                            <Route path="prospects/:id" element={<SalesProspects />} />
                            {/* iter98.53 — Phase 2 routes */}
                            <Route path="tasks" element={<SalesTasks />} />
                            <Route path="analytics" element={<SalesAnalytics />} />
                            <Route path="demo-links" element={<SalesDemoLinks />} />
                            <Route path="settings" element={<Navigate to="/sales" replace />} />
                        </Route>
                    </Routes>
                    <WhatsAppFab />
                    <CookieBanner />
                    </TourProvider>
                </BrowserRouter>
                <Toaster position="top-center" richColors />
                {/* iter98.99an — Universal confirm/alert/prompt host.
                    Replaces native window.confirm/alert/prompt across
                    every portal. Components call confirmDialog() /
                    alertDialog() / promptDialog() from @/lib/confirm. */}
                <DialogHost />
            </AuthProvider>
        </div>
    );
}

export default App;
